// App-wide notifications bell. Shows recent @mentions for the current user.
// Polling-based (useMentions polls every 30s). Server tracks "last seen
// mention id" on users.last_seen_mention_id so unread state syncs across
// devices.

import { useEffect } from 'react';
import { Bell } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { useMarkMentionsSeen, useMentions } from '@/lib/collab';

export function NotificationsBell() {
  const q = useMentions();
  const markSeen = useMarkMentionsSeen();
  const navigate = useNavigate();
  const mentions = q.data?.mentions ?? [];
  const lastSeen = q.data?.lastSeenId ?? 0;
  const unread = mentions.filter((m) => m.id > lastSeen);
  const unreadCount = unread.length;

  // Fire a browser notification when new mentions arrive (and the user has
  // granted permission). We pin the seen-cursor to the top mention so we
  // don't fire repeatedly for the same batch.
  useEffect(() => {
    if (!unreadCount) return;
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'granted') return;
    const top = unread[0];
    if (!top) return;
    new Notification(`${top.authorName} mentioned you`, {
      body: top.body.slice(0, 140),
      tag: `wharf-mention-${top.id}`,
    });
    markSeen.mutate(top.id);
  }, [unreadCount, unread, markSeen]);

  async function requestBrowserNotifs() {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  }

  function openMention(threadId: number, commentId: number) {
    if (commentId > lastSeen) markSeen.mutate(commentId);
    void navigate({
      to: '/inbox',
      search: (prev) => ({ ...(prev as object), thread: threadId }),
    });
  }

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (!open && mentions[0]) markSeen.mutate(mentions[0].id);
        if (open) void requestBrowserNotifs();
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative size-8">
          <Bell className="size-4" strokeWidth={1.75} />
          {unreadCount > 0 && (
            <span
              className={cn(
                'absolute -right-0.5 -top-0.5 inline-flex size-4 items-center justify-center',
                'rounded-full bg-primary text-[9px] font-semibold text-primary-foreground',
              )}
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="border-b px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Mentions
        </div>
        <ScrollArea className="max-h-80">
          {mentions.length === 0 && (
            <p className="px-3 py-4 text-sm text-muted-foreground">
              You haven't been mentioned yet.
            </p>
          )}
          {mentions.map((m) => (
            <DropdownMenuItem
              key={m.id}
              onClick={() => openMention(m.threadId, m.id)}
              className="block whitespace-normal py-2"
            >
              <div className="flex items-baseline justify-between gap-2 text-xs">
                <span className="font-medium text-foreground">
                  {m.authorName || m.authorEmail}
                </span>
                <span className="text-muted-foreground">
                  {new Date(m.createdAt).toLocaleString()}
                </span>
              </div>
              <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">{m.body}</div>
            </DropdownMenuItem>
          ))}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
